/**
 * PlyxTagline — animated brand signature for feed & gallery surfaces.
 *
 * Renders "plyx — where moments become movements" with a slow gradient
 * shimmer that sweeps across the text on a loop. The word "movements"
 * is highlighted with the Plyx brand gradient + a tiny pulsing dot to
 * draw the eye to the promise without being noisy.
 *
 * Variants:
 *   • `variant="feed"`     → top-centre pill on the immersive feed
 *   • `variant="gallery"`  → inline tagline above the profile grid
 *
 * Respects `prefers-reduced-motion` — the shimmer and pulse both
 * freeze for users who opt out at the OS level.
 */
import React from "react";

const PlyxTagline = ({ variant = "feed", className = "" }) => {
  const wrapClass = variant === "feed"
    ? `pointer-events-none select-none inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/35 backdrop-blur border border-white/10 ${className}`
    : `inline-flex items-center gap-1.5 ${className}`;

  return (
    <div
      data-testid={`plyx-tagline-${variant}`}
      className={wrapClass}
    >
      {/* Animated brand wordmark with a continuous left-to-right
          gradient sweep (CSS keyframe animates background-position). */}
      <span className="plyx-tagline-wordmark text-[11px] sm:text-xs uppercase tracking-[0.18em] font-display">
        ▶ plyx
      </span>
      <span className="text-[10px] sm:text-[11px] text-white/55 tracking-wide">
        — where moments become
      </span>
      {/* The pay-off word — gets the full brand gradient plus a
          breathing scale animation so the eye lingers on it. */}
      <span
        className="plyx-tagline-movement text-[10px] sm:text-[11px] font-bold tracking-wide"
        data-testid="plyx-tagline-movement-word"
      >
        movements
      </span>
      {/* Tiny pulsing accent dot to anchor the eye. */}
      <span className="plyx-tagline-dot" aria-hidden="true" />
    </div>
  );
};

export default PlyxTagline;
